import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
    fetchDistributorManufacturerStats,
    fetchDistributorManufacturers,
} from '../../../../services/Http/DistributorApi';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import StatCard from '../../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { MdTrendingUp, MdTrendingDown, MdBusiness } from 'react-icons/md';
import { useCurrency } from '../../../../context/CurrencyProvider';
import { SimpleSelect } from '../../../../shared/layout/forms/AppSelect';

const statsConfig = [
    { key: 'total_manufacturers', label: 'DistributorManufacturers.stats.totalManufacturers', icon: MdBusiness, format: 'number', color: 'manufacturer' },
    { key: 'total_inbound_value', label: 'DistributorManufacturers.stats.inboundValue', icon: MdTrendingDown, format: 'currency', color: 'revenueValue' },
    { key: 'total_outbound_value', label: 'DistributorManufacturers.stats.outboundValue', icon: MdTrendingUp, format: 'currency', color: 'totalVolume' },
];

const periodOptions = ['last_7_days', 'this_month', 'this_quarter', 'this_year'];

const DistributorManufacturers = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const PER_PAGE = 10;
    const [period, setPeriod] = useState('last_7_days');
    const [stats, setStats] = useState(null);
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const periodSelectOptions = periodOptions.map((option) => ({
        value: option,
        label: t(`DistributorManufacturers.periodOption.${option}`),
    }));
    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);

    const loadData = async (page = 1, searchTerm = '', periodFilter = period) => {
        setLoading(true);
        try {
            const [statsResponse, listResponse] = await Promise.all([
                fetchDistributorManufacturerStats(),
                fetchDistributorManufacturers({ page, perPage: PER_PAGE, search: searchTerm, period: periodFilter }),
            ]);
            setStats(statsResponse);
            setList(listResponse.data || []);
            setPagination(listResponse.pagination || { total: 0, current_page: 1, last_page: 1 });
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData(1, '', period);
    }, [period]);

    const handlePeriodChange = (value) => {
        setPeriod(value);
        loadData(1, search, value);
    };

    const handleSearch = (event) => {
        const value = event.target.value;
        setSearch(value);
        loadData(1, value, period);
    };

    const handlePageChange = (direction) => {
        const nextPage = pagination.current_page + direction;
        if (nextPage < 1 || nextPage > pagination.last_page) {
            return;
        }
        loadData(nextPage, search, period);
    };

    const formattedStats = useMemo(() => stats || {}, [stats]);

    if (loading) {
        return <LoadingMessage>{t('DistributorManufacturers.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('DistributorManufacturers.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{t('DistributorManufacturers.title')}</PageTitle>
                    <Subtitle>{t('DistributorManufacturers.subtitle')}</Subtitle>
                </div>
                <ControlRow>
                    <Select
                        value={period}
                        onChange={(event, nextValue) => handlePeriodChange(nextValue ?? event?.target?.value ?? 'last_7_days')}
                        options={periodSelectOptions}
                        inputId="distributor-manufacturers-period"
                    />
                    <SearchInput
                        type="search"
                        value={search}
                        onChange={handleSearch}
                        placeholder={t('DistributorManufacturers.searchPlaceholder')}
                    />
                </ControlRow>
            </PageHeader>
            <PageContent>
                <StatsRow>
                    {statsConfig.map(({ key, label, icon: Icon, format, color }) => (
                        <StatCard
                            key={key}
                            icon={<Icon className="icon" />}
                            title={t(label)}
                            value={formatValue({
                                value: formattedStats[key],
                                localAmount: key === 'total_inbound_value' ? formattedStats.total_inbound_value_local : key === 'total_outbound_value' ? formattedStats.total_outbound_value_local : null,
                                localCurrencyId: key === 'total_inbound_value' ? formattedStats.total_inbound_currency_id : key === 'total_outbound_value' ? formattedStats.total_outbound_currency_id : null,
                                localCurrencyCode: null,
                            }, format, formatAmount)}
                            color={color}
                        />
                    ))}
                </StatsRow>

                <TableCard>
                    <TableHeader>
                        <h4>{t('DistributorManufacturers.table.title')}</h4>
                        <span>{t('DistributorManufacturers.table.count', { count: pagination.total })}</span>
                    </TableHeader>
                    <Table>
                        <thead>
                            <tr>
                                <th>{t('DistributorManufacturers.table.manufacturer')}</th>
                                <th>{t('DistributorManufacturers.table.inbound')}</th>
                                <th>{t('DistributorManufacturers.table.outbound')}</th>
                                <th>{t('DistributorManufacturers.table.retailers')}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((manufacturer) => (
                                <tr key={manufacturer.id}>
                                    <td>{manufacturer.name}</td>
                                    <td>
                                        <ValueStack>
                                            <span>{formatValue({
                                                value: manufacturer.inbound_value,
                                                localAmount: manufacturer.inbound_value_local,
                                                localCurrencyId: manufacturer.inbound_currency_id,
                                                localCurrencyCode: null,
                                            }, 'currency', formatAmount)}</span>
                                            <small>{t('DistributorManufacturers.table.units', { count: manufacturer.inbound_quantity })}</small>
                                        </ValueStack>
                                    </td>
                                    <td>
                                        <ValueStack>
                                            <span>{formatValue({
                                                value: manufacturer.outbound_value,
                                                localAmount: manufacturer.outbound_value_local,
                                                localCurrencyId: manufacturer.outbound_currency_id,
                                                localCurrencyCode: null,
                                            }, 'currency', formatAmount)}</span>
                                            <small>{t('DistributorManufacturers.table.units', { count: manufacturer.outbound_quantity })}</small>
                                        </ValueStack>
                                    </td>
                                    <td>{manufacturer.active_retailers}</td>
                                    <td>
                                        <DetailLink to={`/manufacturers/${manufacturer.id}`}>
                                            {t('DistributorManufacturers.table.view')}
                                        </DetailLink>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination>
                        <button onClick={() => handlePageChange(-1)} disabled={pagination.current_page <= 1}>
                            {t('DistributorManufacturers.pagination.prev')}
                        </button>
                        <span>
                            {pagination.current_page} / {pagination.last_page}
                        </span>
                        <button onClick={() => handlePageChange(1)} disabled={pagination.current_page >= pagination.last_page}>
                            {t('DistributorManufacturers.pagination.next')}
                        </button>
                    </Pagination>
                </TableCard>
            </PageContent>
        </PageContainer>
    );
};

const formatValue = (value, formatType = 'number', formatAmount = (v) => v) => {
    const metric = typeof value === 'object' && value !== null ? value : { value };
    const rawValue = metric.value;

    if (rawValue === null || rawValue === undefined) {
        return '0';
    }

    const numeric = Number(rawValue);
    if (Number.isNaN(numeric)) {
        return rawValue;
    }

    switch (formatType) {
        case 'currency':
            return formatAmount(
                numeric,
                metric.localAmount,
                metric.localCurrencyId,
                metric.localCurrencyCode
            );
        default:
            return numeric.toLocaleString();
    }
};

const ControlRow = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: center;
`;

const Subtitle = styled.p`
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const SearchInput = styled.input`
    border: 1px solid rgba(130, 22, 146, 0.2);
    padding: 0.625rem 0.875rem;
    border-radius: 8px;
    min-width: 240px;
`;

const Select = styled(SimpleSelect)`
    min-width: 180px;
`;

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const TableCard = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    padding: 1.25rem;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        text-align: left;
        padding: 0.75rem 0;
    }

    tbody tr + tr {
        border-top: 1px solid rgba(0, 0, 0, 0.05);
    }
`;

const ValueStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    small {
        color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
    }
`;

const DetailLink = styled(Link)`
    color: #821692;
    font-weight: 600;
`;

const Pagination = styled.div`
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;

    button {
        border: none;
        background: rgba(130, 22, 146, 0.1);
        color: #821692;
        padding: 0.5rem 0.75rem;
        border-radius: 6px;
        cursor: pointer;
    }

    button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export default DistributorManufacturers;
